import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Flag, Save, Loader } from 'lucide-react';
import { metricTracker } from '../../services/MetricTracker';

export default function JobFlaggingModal({ job, onClose, onUpdate }) {
    const [flags, setFlags] = useState({
        isSupervised: job.isSupervised || false,
        isTraining: job.isTraining || false,
        isExceptional: job.isExceptional || false,
        excludeFromMetrics: job.excludeFromMetrics || false
    });
    const [saving, setSaving] = useState(false);

    const handleToggle = (flag) => {
        setFlags(prev => ({
            ...prev,
            [flag]: !prev[flag]
        }));
    };

    const handleSave = async () => {
        setSaving(true);

        try {
            await metricTracker.flagJob(job.id, flags);
            
            if (onUpdate) {
                onUpdate();
            }

            alert('✅ Job flags updated! Contractor metrics will be recalculated.');
            onClose();

        } catch (error) {
            console.error('Error saving job flags:', error);
            alert('Failed to save job flags. Please try again.');
        } finally {
            setSaving(false);
        }
    };

    return (
        <AnimatePresence>
            <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
                <motion.div
                    initial={{ opacity: 0, scale: 0.95 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0, scale: 0.95 }}
                    className="bg-white rounded-xl shadow-xl max-w-lg w-full"
                >
                    {/* Header */}
                    <div className="flex items-center justify-between p-6 border-b border-gray-200">
                        <div className="flex items-center space-x-3">
                            <div className="w-10 h-10 bg-purple-100 rounded-full flex items-center justify-center">
                                <Flag className="w-5 h-5 text-purple-600" />
                            </div>
                            <div>
                                <h2 className="text-xl font-bold text-gray-900">
                                    Flag Job
                                </h2>
                                <p className="text-sm text-gray-600 mt-1">
                                    {job.serviceType}
                                </p>
                            </div>
                        </div>
                        <button
                            onClick={onClose}
                            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                        >
                            <X className="w-5 h-5 text-gray-500" />
                        </button>
                    </div>

                    {/* Content */}
                    <div className="p-6 space-y-4">
                        {/* Supervised Job Toggle */}
                        <div className="flex items-start space-x-3">
                            <input
                                type="checkbox"
                                checked={flags.isSupervised}
                                onChange={() => handleToggle('isSupervised')}
                                className="mt-1 w-5 h-5 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                            />
                            <div className="flex-1">
                                <label className="font-semibold text-gray-900 cursor-pointer">
                                    Supervised Job
                                </label>
                                <p className="text-sm text-gray-600 mt-1">
                                    This job was completed under direct supervision. Counts toward "supervised jobs" metric for Trainee → Junior promotion.
                                </p>
                            </div>
                        </div>

                        {/* Training Job Toggle */}
                        <div className="flex items-start space-x-3">
                            <input
                                type="checkbox"
                                checked={flags.isTraining}
                                onChange={() => handleToggle('isTraining')}
                                className="mt-1 w-5 h-5 text-yellow-600 border-gray-300 rounded focus:ring-yellow-500"
                            />
                            <div className="flex-1">
                                <label className="font-semibold text-gray-900 cursor-pointer">
                                    Training Job
                                </label>
                                <p className="text-sm text-gray-600 mt-1">
                                    This was primarily a training opportunity. Quality issues can be ignored for progression purposes.
                                </p>
                            </div>
                        </div>

                        {/* Exceptional Quality Toggle */}
                        <div className="flex items-start space-x-3">
                            <input
                                type="checkbox"
                                checked={flags.isExceptional}
                                onChange={() => handleToggle('isExceptional')}
                                className="mt-1 w-5 h-5 text-green-600 border-gray-300 rounded focus:ring-green-500"
                            />
                            <div className="flex-1">
                                <label className="font-semibold text-gray-900 cursor-pointer">
                                    Exceptional Quality
                                </label>
                                <p className="text-sm text-gray-600 mt-1">
                                    Contractor went above and beyond. Note this for promotion consideration.
                                </p>
                            </div>
                        </div>

                        {/* Exclude from Metrics Toggle */}
                        <div className="flex items-start space-x-3 pt-4 border-t border-gray-200">
                            <input
                                type="checkbox"
                                checked={flags.excludeFromMetrics}
                                onChange={() => handleToggle('excludeFromMetrics')}
                                className="mt-1 w-5 h-5 text-red-600 border-gray-300 rounded focus:ring-red-500"
                            />
                            <div className="flex-1">
                                <label className="font-semibold text-gray-900 cursor-pointer">
                                    Exclude from Metrics
                                </label>
                                <p className="text-sm text-gray-600 mt-1">
                                    Don't count this job toward any metrics (completion rate, job count, etc.). Use for jobs with extenuating circumstances.
                                </p>
                            </div>
                        </div>

                        {/* Info Box */}
                        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                            <p className="text-sm text-blue-800">
                                💡 <strong>Tip:</strong> Changing these flags will automatically recalculate the contractor's metrics and eligibility for promotion.
                            </p>
                        </div>
                    </div>

                    {/* Footer */}
                    <div className="flex items-center justify-end space-x-3 p-6 border-t border-gray-200">
                        <button
                            onClick={onClose}
                            className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
                        >
                            Cancel
                        </button>
                        <button
                            onClick={handleSave}
                            disabled={saving}
                            className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50"
                        >
                            {saving ? (
                                <>
                                    <Loader className="w-4 h-4 animate-spin" />
                                    <span>Saving...</span>
                                </>
                            ) : (
                                <>
                                    <Save className="w-4 h-4" />
                                    <span>Save Flags</span>
                                </>
                            )}
                        </button>
                    </div>
                </motion.div>
            </div>
        </AnimatePresence>
    );
}
